import React from 'react';
import { SecaoDebitosIcmsDrillDownContentProps } from '../../types/secaoDebitosIcmsDrillDown';
import BaseSecaoDrillDownContent, { 
  BaseSecaoDrillDownData, 
  SecaoDrillDownConfig 
} from '../DrillDown/BaseSecaoDrillDownContent';

/**
 * Configuração específica para drill down de seções com débitos de ICMS
 * Princípio OCP: Extensível sem modificar código existente
 */
const debitosIcmsConfig: SecaoDrillDownConfig = {
  transactionType: 'vendas',
  labels: {
    title: 'Resumo da Seção',
    totalTransaction: 'Total Débitos ICMS',
    partners: 'Clientes',
    products: 'Produtos',
    documents: 'Documentos',
    participation: 'Participação',
    ticketMedio: 'Ticket Médio',
    evolutionTitle: 'Evolução dos Débitos ICMS',
    topPartnersTitle: 'Top 10 Clientes da Seção',
    topProductsTitle: 'Top 10 Produtos da Seção',
    cfopsTitle: 'CFOPs Utilizados',
    ncmTitle: 'Análise por NCM'
  },
  colors: {
    primary: '#d32f2f',
    secondary: '#f44336',
    success: '#2e7d32',
    warning: '#ed6c02',
    info: '#0288d1'
  },
  valueKeys: {
    transactionValue: 'total_transacao',
    partnerValue: 'valor_transacao',
    productValue: 'valor_transacao',
    evolutionValue: 'valor'
  }
};

/**
 * Adaptador para converter dados de débitos ICMS para o formato base
 * Princípio SRP: Responsável apenas pela conversão de dados
 */
const adaptDebitosIcmsDataToBase = (data: any): BaseSecaoDrillDownData => {
  return {
    secao: {
      nome: data.secao?.nome || 'N/A',
      total_transacao: data.secao?.total_debito_icms || 0,
      participacao_percentual: data.secao?.participacao_percentual || 0,
      total_documentos: data.secao?.total_documentos || 0,
      parceiros_distintos: data.secao?.clientes_distintos || 0,
      produtos_distintos: data.secao?.produtos_distintos || 0,
      quantidade_total: data.secao?.quantidade_total || 0,
      ticket_medio: data.secao?.ticket_medio || 0
    },
    evolucao_mensal: (data.evolucao_mensal || []).map((item: any) => ({
      periodo: `${item.mes}/${item.ano}`,
      valor: item.debito_icms,
      documentos: item.total_transacoes || 0,
      parceiros: 0,
      produtos: 0
    })),
    top_parceiros: (data.top_clientes || []).map((cliente: any) => ({
      nome: cliente.nome,
      valor_transacao: cliente.debito_icms,
      valor_total: cliente.vendas,
      participacao_percentual: cliente.participacao_percentual,
      documentos: cliente.documentos,
      produtos_distintos: 0
    })),
    principais_produtos: (data.principais_produtos || []).map((produto: any) => ({
      cod_item: produto.nome,
      descricao_produto: produto.nome,
      valor_transacao: produto.debito_icms,
      valor_total: produto.vendas,
      quantidade: produto.quantidade
    })),
    cfops_utilizados: (data.cfops_utilizados || []).map((cfop: any) => ({
      cfop: cfop.cfop,
      descricao_cfop: cfop.descricao,
      valor_total: cfop.debito_icms,
      documentos: cfop.documentos,
      parceiros: cfop.documentos || 0
    })),
    analise_por_ncm: (data.analise_por_ncm || []).map((ncm: any) => ({
      ncm: ncm.ncm,
      descricao_ncm: ncm.descricao,
      valor_total: ncm.debito_icms,
      quantidade_total: ncm.quantidade,
      produtos_diferentes: 0,
      parceiros_distintos: 0
    }))
  };
};

/**
 * Componente para drill down de seções com débitos de ICMS
 * Princípio SRP: Responsável apenas pela apresentação de drill down de débitos ICMS
 * Princípio OCP: Extensível via configuração
 * Princípio DIP: Depende de abstrações (componente base) não de implementações concretas
 */
const SecaoDebitosIcmsDrillDownContent: React.FC<SecaoDebitosIcmsDrillDownContentProps> = ({ data }) => {
  // Verificar se os dados existem antes de processar
  if (!data) {
    return (
      <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
        Nenhum dado disponível para exibir
      </div>
    );
  }

  // Converter dados para o formato base
  const baseData = adaptDebitosIcmsDataToBase(data);

  return (
    <BaseSecaoDrillDownContent 
      data={baseData} 
      config={debitosIcmsConfig} 
    />
  );
};

export default SecaoDebitosIcmsDrillDownContent; 